import { Tooltip } from 'antd';
import { ReactComponent as IconFuzzyMapping } from 'assets/images/icons/iconFuzzyMapping.svg';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EditDrawerCreateRuleLink } from '../transactions-tab/styles';
import { formatRulePreview } from './rule-preview';
import { buildInterpretationRuleDraftFromTransactionField } from './rule-modal-helpers';
import type {
  InterpretationRuleDraft,
  InterpretationRuleField,
  InterpretationRuleFieldOptions,
} from './types';
import type { ValidatedExcelTransaction } from 'store/organisation/models';

export function buildCaptureRuleLinkTooltip(
  fieldKey: InterpretationRuleField,
  reviewSeed: ValidatedExcelTransaction,
  draftRow: Partial<ValidatedExcelTransaction>,
  t: TFunction,
  fieldOptions: InterpretationRuleFieldOptions
): string {
  const draft = buildInterpretationRuleDraftFromTransactionField(
    fieldKey,
    reviewSeed,
    draftRow,
    fieldOptions
  );
  const preview = formatRulePreview(
    {
      conditions: draft.conditions ?? [],
      actions: draft.actions ?? [],
    },
    t,
    fieldOptions
  );

  if (preview.trim()) {
    return t('organisation.extraction-rules.create-rule-from-field-tooltip', {
      preview,
    });
  }

  return t(
    'organisation.extraction-rules.create-rule-from-field-tooltip-fallback'
  );
}

type CaptureRuleIconLinkProps = {
  tooltipTitle: string;
  onClick: () => void;
  /** Text link instead of icon-only control. */
  iconOnly?: boolean;
  textLabel?: string;
  iconSize?: number;
  className?: string;
};

export const CaptureRuleIconLink: React.FC<CaptureRuleIconLinkProps> = ({
  tooltipTitle,
  onClick,
  iconOnly = true,
  textLabel,
  iconSize = 14,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip
      title={tooltipTitle}
      getPopupContainer={() => document.body}
      mouseEnterDelay={0.25}
    >
      <span
        className="capture-rule-icon-link-tooltip-target"
        style={{ display: 'inline-flex', maxWidth: '100%' }}
      >
        <EditDrawerCreateRuleLink
          type="button"
          className={[
            iconOnly ? 'drawer-create-rule-icon-link' : undefined,
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={
            iconOnly
              ? tooltipTitle
              : textLabel ?? t('organisation.extraction-rules.create-rule-from-field')
          }
          onClick={onClick}
        >
          {iconOnly ? (
            <IconFuzzyMapping
              aria-hidden
              style={{ width: iconSize, height: iconSize }}
            />
          ) : (
            textLabel ?? t('organisation.extraction-rules.create-rule-from-field')
          )}
        </EditDrawerCreateRuleLink>
      </span>
    </Tooltip>
  );
};

export type CreateCaptureRuleIconLinkParams = {
  fieldKey: InterpretationRuleField;
  reviewSeed: ValidatedExcelTransaction;
  draftRow: Partial<ValidatedExcelTransaction>;
  fieldOptions: InterpretationRuleFieldOptions;
  onCreateRule: (draft: Partial<InterpretationRuleDraft>) => void;
  iconOnly?: boolean;
};

export function CreateCaptureRuleIconLink({
  fieldKey,
  reviewSeed,
  draftRow,
  fieldOptions,
  onCreateRule,
  iconOnly = true,
}: CreateCaptureRuleIconLinkParams) {
  const { t } = useTranslation();
  const tooltipTitle = buildCaptureRuleLinkTooltip(
    fieldKey,
    reviewSeed,
    draftRow,
    t,
    fieldOptions
  );

  return (
    <CaptureRuleIconLink
      tooltipTitle={tooltipTitle}
      iconOnly={iconOnly}
      onClick={() =>
        onCreateRule(
          buildInterpretationRuleDraftFromTransactionField(
            fieldKey,
            reviewSeed,
            draftRow,
            fieldOptions
          )
        )
      }
    />
  );
}
